import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import updateGenerationSetup from 'common/src/modules/aiContent/store/aiContentProject/thunks/updateGenerationSetup';
import {RemoteAiContentGenerationSetup} from 'common/src/modules/aiContent/aiContentProject.types';
import {AiContentProjectAgentToolName} from '../aiContentProjectAgent.enums';
import aiImagesApi from 'common/src/modules/aiImages/aiImages.api';
import {GenAIActionType, ProductShotType} from 'common/src/modules/aiImages/aiImages.enums';

// Schema for used input columns
const usedInputColumnSchema = z.object({
  columnName: z.string().describe('Name of the input column to use'),
});

// Schema for image AI generation setup
const imageAiGenerationSetupSchema = z
  .object({
    usedInputColumns: z.array(usedInputColumnSchema).optional().nullable().describe('input columns to use for the image prompt generation, Image type input columns will act as reference images in the generation'),
    useSharedIntermediateImage: z.boolean().optional().describe('Generate single image and smartly crop for all output sizes'),
  })
  .optional()
  .nullable();

// Schema for text generation setup
const textGenerationSetupSchema = z
  .object({
    usedInputColumns: z.array(usedInputColumnSchema).optional().nullable().describe('Input columns to use or referencefor output generation'),
  })
  .optional()
  .nullable();

// Schema for image smart crop setup
const imageSmartCropSetupSchema = z
  .object({
    inputColumnName: z.string().optional().nullable(),
    objectDetectPrompt: z.string().optional().nullable().describe('Name of object to detect in image source and place in the output image focal area, for example \'car\',\'person\''),
    marginPercent: z.number().describe('keep away percentage from the output imagefocal area boundaries'),
  })
  .optional()
  .nullable();

// Enum for product shot type selection
enum ProductShotTypeSelection {
  Product = 'Product',
  Vehicle = 'Vehicle',
}

// Schema for product shot generation setup
const productShotGenerationSetupSchema = z
  .object({
    // Add fields as needed based on RemoteAiContentProductShotSetup
    productImageInputColumnName: z.string().optional().nullable().describe('Which input column, should be used as the product in the image (must be an image type column)'),
    ScenePromptInputColumnName: z.array(usedInputColumnSchema).optional().nullable().describe('input columns to use for the generating the prompt for the scene (either this or the scene reference image should be used)'),
    OptionalSceneReferenceImageInputColumnName: z.string().optional().nullable().describe('if scene uses reference image, which input column should be used for it (must be image type column. either this or prompt input columns should be used)'),
    marginPercent: z.number().describe('keep away percentage from the output imagefocal area boundaries'),
    productType: z.nativeEnum(ProductShotTypeSelection).describe('Type of product shot: Product or Vehicle'),
    useSharedIntermediateImage: z.boolean().optional().describe('Generate single image and smartly crop for all output sizes'),
  })
  .optional()
  .nullable();

// Schema for generation setup
const generationSetupSchema = z.object({
  imageSmartCrop: imageSmartCropSetupSchema,
  imageAiGeneration: imageAiGenerationSetupSchema,
  productShotGeneration: productShotGenerationSetupSchema,
  textGeneration: textGenerationSetupSchema,
});

const inputSchema = z.object({
  contentFeedColumnName: z
    .string()
    .describe('The content feed column key to update the generation setup for'),
  generationSetup: generationSetupSchema.describe(
    'The generation setup configuration for this content feed column. only one type of the subobjects need to be provided',
  ),
});

export default function updateGenerationSetupTool(store: CLStore) {
  async function execute({
    input: {contentFeedColumnName, generationSetup},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const project = state.aiContentProjectPage?.project;

    if (!project?.id) {
      return toolError('No project is currently loaded. Cannot update generation setup.');
    }

    // Validate content feed column exists
    const contentFeedColumns = project.templateMetadata?.contentFeedColumns?.columns;

    if (!contentFeedColumns || !contentFeedColumns[contentFeedColumnName]) {
      return toolError(
        `Content feed column "${contentFeedColumnName}" not found in the project template metadata.`,
      );
    }

    // Fetch vendor services and select the first one for each action type
    let vendorService: number | null = null;
    let vendorServiceWithReferences: number | null = null;

    if (generationSetup.imageAiGeneration) {
      try {
        const servicesPerAction = await aiImagesApi.getServicePerAction();

        // Get first vendor service for PromptToImage
        const promptToImageService = servicesPerAction[GenAIActionType.PromptToImage];

        if (promptToImageService?.services && promptToImageService.services.length > 0) {
          vendorService = promptToImageService.services[0].value;
        }

        // Get first vendor service for PromptToImageWithReferenceImages
        const promptToImageWithRefsService =
          servicesPerAction[GenAIActionType.PromptToImageWithReferenceImages];

        if (
          promptToImageWithRefsService?.services &&
          promptToImageWithRefsService.services.length > 0
        ) {
          vendorServiceWithReferences = promptToImageWithRefsService.services[0].value;
        }
      } catch (error) {
        // If fetching services fails, continue with null values (backend will use defaults)
        console.error('Error fetching vendor services:', error);
      }
    }

    // Convert generation setup to RemoteAiContentGenerationSetup format
    const remoteGenerationSetup: RemoteAiContentGenerationSetup = {
      imageSmartCrop: generationSetup.imageSmartCrop
        ? {
            inputColumnName: generationSetup.imageSmartCrop.inputColumnName ?? null,
            objectDetectPrompt: generationSetup.imageSmartCrop.objectDetectPrompt ?? null,
            marginPercent: generationSetup.imageSmartCrop.marginPercent,
          }
        : null,
      imageAiGeneration: generationSetup.imageAiGeneration
        ? {
            usedInputColumns: generationSetup.imageAiGeneration.usedInputColumns
              ? generationSetup.imageAiGeneration.usedInputColumns.map((col) => ({
                  columnName: col.columnName,
                }))
              : null,
            vendorService,
            vendorServiceWithReferences,
            useSharedIntermediateImage:
              generationSetup.imageAiGeneration.useSharedIntermediateImage ?? false,
          }
        : null,
      productShotGeneration:
        generationSetup.productShotGeneration &&
        Object.keys(generationSetup.productShotGeneration).length > 0
          ? {
              productImageInputColumnName:
                generationSetup.productShotGeneration.productImageInputColumnName ?? null,
              ScenePromptInputColumnName: generationSetup.productShotGeneration.ScenePromptInputColumnName
                ? generationSetup.productShotGeneration.ScenePromptInputColumnName.map((col) => ({
                    columnName: col.columnName,
                  }))
                : null,
              OptionalSceneReferenceImageInputColumnName:
                generationSetup.productShotGeneration.OptionalSceneReferenceImageInputColumnName ?? null,
              marginPercent: generationSetup.productShotGeneration.marginPercent,
              productType:
                generationSetup.productShotGeneration.productType === ProductShotTypeSelection.Product
                  ? ProductShotType.Product
                  : ProductShotType.Vehicle,
              useSharedIntermediateImage:
                generationSetup.productShotGeneration.useSharedIntermediateImage ?? false,
            }
          : null,
      textGeneration: generationSetup.textGeneration
        ? {
            usedInputColumns: generationSetup.textGeneration.usedInputColumns
              ? generationSetup.textGeneration.usedInputColumns.map((col) => ({
                  columnName: col.columnName,
                }))
              : null,
          }
        : null,
    };

    try {
      await store
        .dispatch(
          updateGenerationSetup({
            projectId: project.id,
            request: {
              contentFeedColumnName,
              generationSetup: remoteGenerationSetup,
            },
          }),
        )
        .unwrap();

      return toolResult(
        `Successfully updated generation setup for content feed column "${contentFeedColumnName}".`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolError(`Error updating generation setup: ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Updating generation setup',
    name: AiContentProjectAgentToolName.UpdateGenerationSetup,
    description:
      'Updates the output generation setup configuration for a specific content feed column. This controls how AI generates content for that column (e.g., which input columns to use, which vendor service, etc.).',
    execute,
    inputSchema,
  });
}

